import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Button } from 'antd';
import { ScheduleOutlined } from '@ant-design/icons';
import { TournamentFormatEnum, TournamentType } from '../../../enums/enums';
import { COLORS, FONT_WEIGHTS, TEXT_SIZES } from '../../../utils/constants';
import SportTypeModal from './SportTypeModal';
import { useSnackbar } from '../../../contexts/SnackbarContext/SnackbarProvider';
import { useEventContext } from '../../../contexts/EventContext';
import { generateBracketRequest } from '../../../api/requests/tournaments';

const SinglesGenerateBracketScreen: React.FC = () => {
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const { tournamentId } = useParams();
  const { tournamentType, eventData, refreshEventData } = useEventContext();
  const [isBracketModalVisible, setIsBracketModalVisible] =
    useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const handleGenerateBracket = async (
    selectedFormat: TournamentFormatEnum,
  ) => {
    try {
      setLoading(true);
      const response = await generateBracketRequest(
        Number(tournamentId),
        selectedFormat,
      );
      if (response && response.isSuccess) {
        snackbar.success('Bracket generated successfully!');
        setIsBracketModalVisible(false);
        // Navigate to brackets view after successful generation
        navigate('../../singles-brackets', { relative: 'path' });
        refreshEventData();
      } else {
        snackbar.error(response?.error);
      }
    } catch (error) {
      console.error('Error generating bracket:', error);
      snackbar.error('Failed to generate bracket. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const parsedTournamentType = tournamentType
    ? parseInt(tournamentType)
    : TournamentType.Singles;

  const getPlayersCount = () => {
    const tournamentData = eventData?.tournamentSummaries.filter(
      (data) => data.tournamentId === Number(tournamentId),
    );
    return tournamentData ? tournamentData[0].participantCount : 0;
  };

  return (
    <Container>
      <Content>
        <IconCircle>
          <ScheduleOutlined />
        </IconCircle>
        <MainTitle>Ready to Start Tournament</MainTitle>
        <Description>
          {getPlayersCount()} players are registered. Click shuffle to randomly
          pair players and generate the tournament bracket.
        </Description>
        <Button
          type="primary"
          size="large"
          loading={loading}
          onClick={() => setIsBracketModalVisible(true)}
        >
          Generate brackets
        </Button>
      </Content>
      {isBracketModalVisible && (
        <SportTypeModal
          visible={isBracketModalVisible}
          tournamentType={parsedTournamentType}
          onProceed={(selectedFormat) => {
            handleGenerateBracket(selectedFormat);
          }}
          closeModal={() => setIsBracketModalVisible(false)}
        />
      )}
    </Container>
  );
};

export default SinglesGenerateBracketScreen;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: ${COLORS.white.primary};
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 16px;
  overflow: hidden;
`;

const IconCircle = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  border: 1px solid ${COLORS.skyBlue.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  color: ${COLORS.skyBlue.primary};
  margin-bottom: 20px;
`;

const MainTitle = styled.div`
  font-size: ${TEXT_SIZES.MD}px;
  font-weight: ${FONT_WEIGHTS.BOLD};
  margin-bottom: 12px;
`;

const Description = styled.div`
  font-size: ${TEXT_SIZES.SM}px;
  text-align: center;
  margin-bottom: 20px;
  max-width: 260px;
  color: ${COLORS.grey.dark};
`;
